import { Link } from 'react-router-dom';
import SEO from '@/components/base/SEO';
import ServicePageLayout from '@/components/base/ServicePageLayout';

const breadcrumb = [
  { label: 'Accueil', to: '/' },
  { label: 'Dépannage menuiseries Pérols' },
];

const jsonLdService = {
  '@context': 'https://schema.org',
  '@type': 'Service',
  name: 'Dépannage et réparation de menuiseries à Pérols',
  provider: { '@id': 'https://heraultroulettesbaiesvitrees.fr/#business' },
  areaServed: { '@type': 'City', name: 'Pérols' },
  description: 'Réparation de baies coulissantes, volets roulants, vitrages, stores et fenêtres à Pérols. Intervention 24-48h, 7j/7. Devis gratuit, garantie 10 ans.',
  offers: { '@type': 'Offer', price: '0', priceCurrency: 'EUR', description: 'Devis gratuit' },
};

const jsonLdFAQ = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'Intervenez-vous près de l\'aéroport et d\'Odysseum à Pérols ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Oui, nous intervenons dans tout Pérols, y compris les secteurs proches d\'Odysseum, de l\'aéroport et du littoral. Les résidences récentes du secteur sont souvent équipées de baies coulissantes et de volets électriques que nous maîtrisons parfaitement.',
      },
    },
    {
      '@type': 'Question',
      name: 'Réparez-vous les volets roulants électriques à Pérols ?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Absolument. Les volets roulants électriques sont très répandus à Pérols. Nous diagnostiquons pannes moteur, télécommande, condensateur et réparons sur place. Délai 24-48h, devis gratuit.',
      },
    },
  ],
};

export default function PerolsPage() {
  return (
    <>
      <SEO
        title="Réparation baie coulissante Pérols | DFPRO"
        description="Baie coulissante, volet roulant électrique en panne à Pérols ? DFPRO intervient 24-48h, 7j/7. Odysseum, aéroport, Carnon. Devis gratuit, garantie 10 ans."
        canonicalUrl="https://heraultroulettesbaiesvitrees.fr/depannage-menuiserie-perols/"
      />
      <ServicePageLayout
        heroImage="/images/ville-herault-01.jpg"
        title="Réparation de menuiseries et baie coulissante à Pérols"
        subtitle="Entre Montpellier et la mer, Pérols concentre un habitat récent. Nos techniciens réparent baies, volets électriques et vitrages. Devis gratuit, 24-48h, 7j/7."
        breadcrumb={breadcrumb}
        jsonLdService={jsonLdService}
        jsonLdFAQ={jsonLdFAQ}
        ctaTitle="Volet électrique ou baie en panne à Pérols ?"
        ctaText="Devis gratuit, intervention 24-48h, garantie 10 ans. Nos techniciens se déplacent à Pérols et aux alentours, 7j/7."
        ctaServiceLabel="Tous nos services"
      >
        <p className="text-lg text-secondary-500 leading-relaxed">
          Pérols, entre Montpellier et le littoral, compte de nombreuses résidences récentes avec baies coulissantes et volets roulants électriques. Nous intervenons pour toute panne : moteur, roulettes, vitrage. Appelez le <a href="tel:+33980802242" className="text-accent-500 font-semibold hover:underline">09 80 80 22 42</a>.
        </p>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/perols-baie-01.jpg"
              alt="Réparation de baie vitrée coulissante à Pérols"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Baie coulissante : entretien des résidences récentes</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les résidences de Pérols sont largement équipées de baies coulissantes. Proximité du littoral, chaleur estivale : les roulettes fatiguent et la baie <Link to="/baie-coulissante-qui-coince-herault/" className="text-accent-500 font-semibold hover:underline">coince</Link>. Un remplacement préventif évite les dégâts sur le rail.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/changer-roulettes-galets-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">Roulettes</Link>, <Link to="/reparation-rail-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">rail</Link>, <Link to="/reglage-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">réglage</Link>, <Link to="/remplacement-baie-coulissante-herault/" className="text-accent-500 font-semibold hover:underline">remplacement</Link>.
            </p>
            <Link to="/reparation-baie-coulissante-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage baie coulissante <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Volet roulant électrique : diagnostic et réparation</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              Les <Link to="/volet-roulant-electrique-herault/" className="text-accent-500 font-semibold hover:underline">volets roulants électriques</Link> sont la norme à Pérols. Moteur silencieux qui ne tourne plus, télécommande qui ne répond pas, <Link to="/volet-roulant-bloque-herault/" className="text-accent-500 font-semibold hover:underline">volet bloqué</Link> : nous diagnostiquons.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-moteur-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">Changement moteur</Link>, <Link to="/motorisation-volet-roulant-herault/" className="text-accent-500 font-semibold hover:underline">motorisation</Link>, <Link to="/volet-roulant-ne-remonte-plus-herault/" className="text-accent-500 font-semibold hover:underline">volet qui ne remonte plus</Link>.
            </p>
            <Link to="/reparation-volet-roulant-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Dépannage volet roulant <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/perols-volet-02.jpg"
              alt="Réparation de volet roulant électrique à Pérols"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
        </div>

        <div className="not-prose my-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="w-full h-[280px] md:h-[340px] rounded-lg overflow-hidden">
            <img
              src="/images/perols-vitrage-03.webp"
              alt="Remplacement de vitrage à Pérols"
              className="w-full h-full object-cover"
              loading="lazy"
            />
          </div>
          <div>
            <h3 className="text-xl font-heading font-bold text-primary-500 mb-3">Vitrier et fenêtre</h3>
            <p className="text-secondary-500 mb-3 leading-relaxed">
              <Link to="/vitrier-herault/" className="text-accent-500 font-semibold hover:underline">Vitrier</Link> : <Link to="/remplacement-vitre-cassee-herault/" className="text-accent-500 font-semibold hover:underline">vitre cassée</Link>, <Link to="/remplacement-double-vitrage-herault/" className="text-accent-500 font-semibold hover:underline">double vitrage</Link>, <Link to="/double-vitrage-embue-herault/" className="text-accent-500 font-semibold hover:underline">vitrage embué</Link>. <Link to="/vitrier-urgence-herault/" className="text-accent-500 font-semibold hover:underline">Urgence</Link> 7j/7.
            </p>
            <p className="text-secondary-500 mb-4 leading-relaxed">
              <Link to="/reparation-fenetre-herault/" className="text-accent-500 font-semibold hover:underline">Fenêtres</Link>, <Link to="/reparation-store-herault/" className="text-accent-500 font-semibold hover:underline">stores</Link>, <Link to="/reparation-bso-herault/" className="text-accent-500 font-semibold hover:underline">BSO</Link>.
            </p>
            <Link to="/vitrier-herault/" className="inline-flex items-center gap-2 text-accent-500 font-semibold hover:underline">
              Service vitrier <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <h2>Intervention à Pérols sous 24 à 48h</h2>
        <p>
          Nous couvrons tout Pérols : centre, Odysseum, abords de l'aéroport, zones résidentielles. Communes voisines : <Link to="/depannage-menuiserie-montpellier/" className="text-accent-500 font-semibold hover:underline">Montpellier</Link>, <Link to="/depannage-menuiserie-lattes/" className="text-accent-500 font-semibold hover:underline">Lattes</Link>, <Link to="/depannage-menuiserie-mauguio/" className="text-accent-500 font-semibold hover:underline">Mauguio</Link>. <Link to="/" className="text-accent-500 font-semibold hover:underline">Accueil DFPRO</Link>.
        </p>
      </ServicePageLayout>
    </>
  );
}